// ── UNITS — every number on a page says what it is counted in ────────────────
//
// The rules (owner approved):
//   - grain is bushels                    -> "bu/ac", "bu"
//   - silage is WET tons per acre         -> "wet ton/ac", "wet tons"
//   - dry beans are pounds per acre       -> "lb/ac", "lb"
//   - silage and grain are never merged: corn silage (RMA type 026, or a crop
//     name with SILAGE in it) never reads as bushels, and a "bu/ac" stamped on
//     silage is refused (null), never printed.
//   - anything else (hay, potatoes, apples, onions ...) shows the unit the data
//     itself recorded, or null. The kit never guesses a unit.
//
// Display only. Stored units are never changed.

// The server stores silage as wet tons standardised to 35% dry matter and tags it
// "ton/ac @35% DM". Farmers read it as wet tons per acre (Kevin, 2026-11-01).
// Moved here from App.jsx unchanged; App.jsx imports it.
export const silageUnitLabel = (u) => (u === "ton/ac @35% DM" ? "wet ton/ac" : u);

// What a caller prints when unitLabel answers null.
export const UNIT_UNKNOWN = "unit not recorded";

// RMA commodity codes, so "0041" and "CORN" are the same crop here.
const CODE_NAMES = { "0011": "WHEAT", "0016": "OATS", "0041": "CORN", "0047": "DRY BEANS", "0051": "GRAIN SORGHUM",
  "0081": "SOYBEANS", "0091": "BARLEY", "0158": "TRITICALE" };
const GRAIN = new Set(["CORN", "SOYBEANS", "WHEAT", "OATS", "BARLEY", "GRAIN SORGHUM", "SORGHUM", "TRITICALE", "RYE", "SPELT"]);
const CORN_SILAGE_TYPE = "026";

const cropKey = (crop) => {
  const s = String(crop == null ? "" : crop).trim().toUpperCase().replace(/_/g, " ");
  const k = CODE_NAMES[s] || CODE_NAMES[s.padStart(4, "0")] || s;
  // "Corn grain" is corn; "Soybean" is soybeans; "Dry bean" is dry beans.
  return k === "SOYBEAN" ? "SOYBEANS" : k === "DRY BEAN" ? "DRY BEANS" : k.replace(/^(CORN|WHEAT|OATS|BARLEY) GRAIN$/, "$1");
};

// Silage? A name with SILAGE in it, or RMA corn with type code 026.
export const isSilageCrop = (crop, typeCode) => {
  const k = cropKey(crop);
  if (/SILAGE/.test(k)) return true;
  return k === "CORN" && String(typeCode == null ? "" : typeCode).padStart(3, "0") === CORN_SILAGE_TYPE;
};
export const isDryBeanCrop = (crop) => cropKey(crop) === "DRY BEANS";
export const isGrainCrop = (crop, typeCode) => !isSilageCrop(crop, typeCode) && GRAIN.has(cropKey(crop));

const LONG = { "bu/ac": "bushels per acre", "wet ton/ac": "wet tons per acre", "lb/ac": "pounds per acre",
  "bu": "bushels", "wet tons": "wet tons", "lb": "pounds" };
const perAcreToTotal = (u) => (u === "wet ton/ac" ? "wet tons" : String(u).replace(/\s*\/\s*ac$/i, ""));

// unitLabel(crop, kind, { typeCode, unit })
//   kind: "yield" (per acre, default) | "total" | "long" (spelled out, per acre)
//   typeCode: RMA type code, so CORN 026 reads as silage
//   unit: the unit the row itself recorded, when it has one
// Returns a short label, or null when nothing establishes one.
export function unitLabel(crop, kind = "yield", opts = {}) {
  const { typeCode = null, unit = null } = opts || {};
  const stored = unit == null || unit === "" || unit === UNIT_UNKNOWN ? null : String(unit).trim();
  let perAcre;
  if (isSilageCrop(crop, typeCode)) {
    // A bushel stamped on silage is a guess from somewhere else: refuse it.
    if (stored && /\bbu\b/i.test(stored)) return null;
    // Known wet-ton forms read as wet tons; any other stated unit is shown as stated.
    const wet = !stored || ["ton/ac", "wet ton/ac", "ton/ac @35% DM", "tons/ac", "t/ac"].includes(stored);
    perAcre = wet ? "wet ton/ac" : silageUnitLabel(stored);
  } else if (stored) {
    perAcre = silageUnitLabel(stored);
  } else if (isDryBeanCrop(crop)) {
    perAcre = "lb/ac";
  } else if (GRAIN.has(cropKey(crop))) {
    perAcre = "bu/ac";
  } else {
    return null;
  }
  if (kind === "total") return perAcreToTotal(perAcre);
  if (kind === "long") return LONG[perAcre] || perAcre;
  return perAcre;
}

// "182.4 bu/ac", or the number alone with UNIT_UNKNOWN beside it. Never a bare number.
export function withUnit(value, crop, kind = "yield", opts = {}) {
  if (value == null || value === "") return "—";
  const u = unitLabel(crop, kind, opts);
  return u ? `${value} ${u}` : `${value} (${UNIT_UNKNOWN})`;
}
